import axios from 'axios';
import {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';

/**
 * Centage Axios Instance is a decorator that wraps the AxiosInstance to add response postprocessor.
 * All methods are just delegated to Axios, only the response is sent to postprocessor before it is returned to caller.
 * In oppose to standard interceptor, postprocesser can modify, replace or completely consume the response or error (whatever postprocessor returns will be returned to caller).
 */
class CentageAxiosInstance {
  constructor() {
    this.axiosApi = axios.create({
      baseURL: `/api`,
    });
    this.defaults = this.axiosApi.defaults;
    this.interceptors = this.axiosApi.interceptors;
    this.interceptors.responsePostprocessor = null;
  }

  getUri(config) {
    return this.axiosApi.getUri(config);
  }

  request(config) {
    return this.responsePostprocessorHandler(this.axiosApi.request(config));
  }

  get(url, config) {
    return this.responsePostprocessorHandler(this.axiosApi.get(url, config));
  }

  delete(url, config) {
    return this.responsePostprocessorHandler(this.axiosApi.delete(url, config));
  }

  head(url, config) {
    return this.responsePostprocessorHandler(this.axiosApi.head(url, config));
  }

  options(url, config) {
    return this.responsePostprocessorHandler(this.axiosApi.options(url, config));
  }

  post(url, data, config) {
    return this.responsePostprocessorHandler(this.axiosApi.post(url, data, config));
  }

  put(url, data, config) {
    return this.responsePostprocessorHandler(this.axiosApi.put(url, data, config));
  }

  patch(url, data, config) {
    return this.responsePostprocessorHandler(this.axiosApi.patch(url, data, config));
  }

  responsePostprocessorHandler(apiResponse) {
    if (this.interceptors.responsePostprocessor) {
      return new Promise((resolve, reject) => {
        apiResponse
          .then(responseData => {
            resolve(responseData);
          })
          .catch(error => {
            if (error && error.response && error.response.status) {
              if (error.response.status === 412) {
                // To handle Sophos issue we are intercepting the response and if the status code is 412
                // we throw pre-condition failed error.
                // setting the status because we display the error status everywhere across the product
                // exception is caused by duplicating the same request, notify user by message, but consume the exception, so UI can wait for the original request
                messageService.showHideMessage(STRING_REF.error, t('SC_PRECONDITION_FAILED'));
                return;
              } else if (error.response.status === 401) {
                if (window.location.pathname !== CommonService.APP_BASE + '/login') {
                  CommonService.redirectByState('session_timeout');
                  return;
                }
              }
            }
            reject(error);
          });
      });
    }
    return apiResponse;
  }
}

const apiInstance = new CentageAxiosInstance();

const AxiosInterceptor = ({ props, children }) => {
  const [isSet, setIsSet] = useState(false);

  const { t } = useTranslation('common');
  const {
    services: { CommonService, messageService, STRING_REF },
  } = props;

  useEffect(() => {
    const requestInterceptor = apiInstance.interceptors.request.use(
      function (config) {
        config.url = config.url.replace(/[?|&]centageId=\d+/, '');
        config.url += config.url.indexOf('?') === -1 ? '?' : '&';
        config.url += 'centageId=' + ((Date.now() + Math.random()) * 0x10000).toString(16);

        return config;
      },
      function (error) {
        return Promise.reject(error);
      }
    );

    apiInstance.interceptors.responsePostprocessor = function (apiResponse) {
      return new Promise((responseHandlerResolve, responseHandlerReject) => {
        apiResponse
          .then(responseData => {
            responseHandlerResolve(responseData);
          })
          .catch(error => {
            if (error && error.response && error.response.status) {
              if (error.response.status === 412) {
                messageService.showHideMessage(STRING_REF.error, t('SC_PRECONDITION_FAILED'));
                return;
              } else if (error.response.status === 401) {
                if (window.location.pathname !== CommonService.APP_BASE + '/login') {
                  CommonService.redirectByState('session_timeout');
                  return;
                }
              }
            }
            responseHandlerReject(error);
          });
      });
    };

    setIsSet(true);

    return () => {
      apiInstance.interceptors.request.eject(requestInterceptor);
      apiInstance.interceptors.responsePostprocessor = null;
    };
  }, []);

  return isSet && children;
};

export {CentageAxiosInstance};
export default apiInstance;
export {AxiosInterceptor};
